// Milestone write operations for the EXT supplier portal.
//
// JSON writes that go through the EXT proxy chain
// (browser → bldtext-fn /ProxiesV2 → bldt-api-fn /proxiesV2 → bldt-ui-fn
// /api/v2/{endpoint}). Reads are still done with useFetch; this module
// is write-only.

import { SectionResponse } from '../@types';
import { jsonRequest } from '../utils/request';

const API_URL = process.env.REACT_APP_API_URL;

// PATCH (or POST) section responses for one milestone. When `voucherId`
// is supplied we PATCH the existing voucher; otherwise we POST a new
// one. The server is the source of truth for supplier-completion +
// sequencing + lock-after-complete; this call assumes those rules pass.
export const submitMilestoneResponses = async (
	conditionId: number,
	sectionResponses: SectionResponse[],
	options: {
		draft: boolean;
		voucherId?: number | null;
		invoice?: {
			invoiceNumber: string | null;
			invoiceDate: string | null;
			invoiceAmount: number | null;
			invoiceCurrency: string | null;
		};
	}
): Promise<unknown> => {
	// Section status is the PIU verification verdict, not the supplier's
	// save-vs-submit intent. Save Draft → PENDING (still awaiting review),
	// Submit Milestone → SUBMITTED (handed to PIU). VERIFIED/REJECTED are
	// owned by the PIU and are never set by the supplier.
	const status: 'PENDING' | 'SUBMITTED' = options.draft ? 'PENDING' : 'SUBMITTED';
	const stamped: SectionResponse[] = sectionResponses.map((sr) => ({
		...sr,
		conditionId,
		status,
	}));

	const payload = {
		conditionId,
		sectionResponses: stamped,
		// When the milestone submission carries an invoice, stamp its number,
		// date, amount and currency onto the voucher so those fields are no
		// longer null on the payment record.
		...(options.invoice && {
			invoiceNumber: options.invoice.invoiceNumber,
			invoiceDate: options.invoice.invoiceDate,
			invoiceAmount: options.invoice.invoiceAmount,
			invoiceCurrency: options.invoice.invoiceCurrency,
		}),
	};

	if (options.voucherId != null) {
		return jsonRequest(`${API_URL}/api/ProxiesV2/vouchers/${options.voucherId}`, {
			method: 'PATCH',
			body: payload,
		});
	}
	return jsonRequest(`${API_URL}/api/ProxiesV2/vouchers`, {
		method: 'POST',
		body: { ...payload, conditionId },
	});
};
